import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { moneyCompact, perWatt } from "@/lib/format";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Pencil, Trophy, ThumbsDown } from "lucide-react";
import { celebrateNow } from "@/components/WinCelebration";

const stop = (e) => e.stopPropagation();

// Inline lump-sum edit on a priced-elsewhere pipeline card. The prior value moves into price history.
export function QuickLumpEdit({ item, onSaved }) {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState("");
  const [saving, setSaving] = useState(false);
  const n = Number(val);
  const cpw = n > 0 && item.capacity_mw ? n / (Number(item.capacity_mw) * 1_000_000) : null;

  const onOpen = (o) => { setOpen(o); if (o) setVal(item.stored_total ?? ""); };
  const save = async () => {
    if (!(n > 0)) { toast.error("Enter the fully-loaded lump-sum price."); return; }
    setSaving(true);
    try {
      await api.post(`/bids/${item.id}/external-update`, { lump_sum: n });
      toast.success(`Lump sum updated to ${moneyCompact(n)} — prior price kept in history`);
      setOpen(false);
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
    finally { setSaving(false); }
  };

  return (
    <Popover open={open} onOpenChange={onOpen}>
      <PopoverTrigger asChild>
        <button type="button" onClick={stop} data-testid={`quick-lump-${item.id}`} title="Edit lump sum"
          className="group/lump inline-flex items-center gap-1 text-xs font-mono font-medium text-slate-700 hover:text-indigo-700 border-b border-dashed border-slate-300 hover:border-indigo-400 transition-colors">
          {moneyCompact(item.stored_total)}
          <Pencil className="w-2.5 h-2.5 text-slate-400 group-hover/lump:text-indigo-600" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-3" onClick={stop} data-testid={`quick-lump-pop-${item.id}`}>
        <div className="text-xs font-semibold text-slate-800">Update lump sum</div>
        <div className="text-[11px] text-slate-500 mb-2">Fully-loaded price. The current number moves into price history.</div>
        <Input type="number" step="any" autoFocus value={val} data-testid={`quick-lump-input-${item.id}`}
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setOpen(false); }}
          className="h-8 font-mono" />
        <div className="text-[10px] text-slate-400 mt-1 h-3">{cpw ? perWatt(cpw) : ""}</div>
        <div className="flex justify-end gap-2 mt-2">
          <Button size="sm" variant="ghost" onClick={() => setOpen(false)} data-testid={`quick-lump-cancel-${item.id}`}>Cancel</Button>
          <Button size="sm" onClick={save} disabled={saving} data-testid={`quick-lump-save-${item.id}`} className="bg-[#003E87] hover:bg-[#00306a] text-white">
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// One-tap Won / Lost on a submitted priced-elsewhere card. Won records instantly (with undo);
// Lost opens the outcome dialog because a loss reason is required.
export function QuickOutcome({ item, onChanged, onLost }) {
  const [busy, setBusy] = useState(false);
  const won = async (e) => {
    stop(e);
    setBusy(true);
    try {
      await api.post(`/bids/${item.id}/outcome`, { outcome: "won" });
      celebrateNow();
      toast.success(`Marked “${item.name}” won`, {
        action: {
          label: "Undo",
          onClick: async () => {
            try { await api.post(`/bids/${item.id}/reopen`, { status: "sent" }); onChanged?.(); }
            catch { toast.error("Could not undo"); }
          },
        },
      });
      onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not record the win"); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-2 grid grid-cols-2 gap-1.5" data-testid={`quick-outcome-${item.id}`}>
      <button type="button" onClick={won} disabled={busy} data-testid={`quick-won-${item.id}`}
        className="inline-flex items-center justify-center gap-1 h-7 rounded-md border border-emerald-200 text-emerald-700 text-[11px] font-semibold hover:bg-emerald-500 hover:text-white hover:border-emerald-500 transition-colors disabled:opacity-50">
        <Trophy className="w-3 h-3" /> Won
      </button>
      <button type="button" onClick={(e) => { stop(e); onLost?.(item); }} disabled={busy} data-testid={`quick-lost-${item.id}`}
        className="inline-flex items-center justify-center gap-1 h-7 rounded-md border border-rose-200 text-rose-700 text-[11px] font-semibold hover:bg-rose-600 hover:text-white hover:border-rose-600 transition-colors disabled:opacity-50">
        <ThumbsDown className="w-3 h-3" /> Lost
      </button>
    </div>
  );
}
